// src/FacebookDownloader.js
import React, { useState } from 'react';
import { apiFetch } from './utils/apiUtils';
import {
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Paper,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Grid
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const FacebookDownloader = () => {
  // State for date range
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // State for using previous month
  const [usePreviousMonth, setUsePreviousMonth] = useState(true);
  
  // Status and error messages
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  
  // Loading state
  const [loading, setLoading] = useState(false);
  
  // Statistics
  const [stats, setStats] = useState(null);

  // Set default dates to previous month when component mounts
  React.useEffect(() => {
    if (usePreviousMonth) {
      setDefaultDatesToPreviousMonth();
    }
  }, [usePreviousMonth]);

  // Function to set default dates to previous month
  const setDefaultDatesToPreviousMonth = () => {
    const today = new Date();
    const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfPreviousMonth = new Date(firstDayOfCurrentMonth);
    lastDayOfPreviousMonth.setDate(lastDayOfPreviousMonth.getDate() - 1);
    const firstDayOfPreviousMonth = new Date(lastDayOfPreviousMonth.getFullYear(), lastDayOfPreviousMonth.getMonth(), 1);
    
    setStartDate(firstDayOfPreviousMonth);
    setEndDate(lastDayOfPreviousMonth);
  };

  // Toggle using previous month
  const handlePreviousMonthToggle = (event) => {
    setUsePreviousMonth(event.target.checked);
    if (event.target.checked) {
      setDefaultDatesToPreviousMonth();
    }
  };

  // Handle download button click
  const handleDownload = async () => {
    // Reset state
    setError("");
    setMessage("");
    setStats(null);
    
    // Validate dates
    if (usePreviousMonth || (startDate && endDate)) {
      try {
        setLoading(true);
        
        // Format dates for API request
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);
        
        // Get token from localStorage
        const token = localStorage.getItem("token");
        
        // Call API endpoint using apiFetch which automatically handles authorization headers and URL resolution
        const response = await apiFetch(`/api/integrations/facebook/messages`, {
          method: "POST",
          body: JSON.stringify({ 
            startDate: formattedStartDate, 
            endDate: formattedEndDate,
            usePreviousMonth
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to download Facebook messages");
        }
        
        const result = await response.json();
        setMessage(result.message || "Facebook messages downloaded successfully!");
        setStats(result.stats);
      } catch (err) {
        setError(err.message || "An error occurred while downloading Facebook messages");
      } finally {
        setLoading(false);
      }
    } else {
      setError("Please select a valid date range or use the previous month option.");
    }
  };

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>Facebook Message Downloader</Typography>
      <Typography variant="body1" paragraph>
        This tool downloads Facebook messages for the specified date range and stores them in the database.
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Date Range Selection</Typography>
        
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={usePreviousMonth} 
                onChange={handlePreviousMonthToggle}
              />
            }
            label="Use Previous Month"
          />
        </Box>
        
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
                disabled={usePreviousMonth}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
                disabled={usePreviousMonth}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleDownload}
            disabled={loading || (!usePreviousMonth && (!startDate || !endDate))}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? "Downloading..." : "Download Facebook Messages"}
          </Button>
        </Box>
      </Paper>
      
      {stats && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Download Results</Typography>
          <Typography variant="body1">
            <strong>Date Range:</strong> {formatDate(startDate)} to {formatDate(endDate)}
          </Typography>
          <Typography variant="body1">
            <strong>Total Conversations:</strong> {stats.totalConversations}
          </Typography>
          <Typography variant="body1">
            <strong>Total Messages:</strong> {stats.totalMessages}
          </Typography>
          <Typography variant="body1">
            <strong>Messages Saved:</strong> {stats.messagesSaved}
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default FacebookDownloader;